import { buildPaymentRoadmap, type PaymentCondition } from '../paymentCondition';

export type DueItem = { label: string; dueMs: number; amount: number };
export type UnpaidItem = DueItem & { unpaid: number };

const fromYmd = (value?: string): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : 0;
};

export const toYmd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const hasStages = (condition: any): condition is PaymentCondition =>
  !!condition && Array.isArray(condition.stages) && condition.stages.length > 0;

/**
 * Dated demands for one booking: each stage or monthly instalment's share of the unit price.
 * Possession-linked stages have no date yet, so they are left out. Returns null when the booking
 * has no structured payment plan.
 */
export function buildDueSchedule(condition: any, bookingDateYmd: string, unitAmount: number): DueItem[] | null {
  if (!hasStages(condition) || !(unitAmount > 0) || !fromYmd(bookingDateYmd)) return null;
  const items: DueItem[] = [];
  buildPaymentRoadmap(condition, bookingDateYmd).forEach((stage) => {
    if (stage.instalments?.length) {
      stage.instalments.forEach((row) => {
        const dueMs = fromYmd(row.dueDate);
        if (dueMs) items.push({ label: `${stage.label}, ${row.label}`, dueMs, amount: Math.round((unitAmount * row.percent) / 100) });
      });
      return;
    }
    const dueMs = fromYmd(stage.dueDate);
    if (dueMs && stage.percent > 0) items.push({ label: stage.label, dueMs, amount: Math.round((unitAmount * stage.percent) / 100) });
  });
  return items.sort((a, b) => a.dueMs - b.dueMs);
}

/**
 * Applies what the buyer has paid to the demands in date order, so a part payment clears the oldest
 * demand first. Overdue = unpaid on demands dated before today.
 */
export function unitDues(schedule: DueItem[], received: number, now: Date) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let left = Math.max(0, received);
  const unpaid: UnpaidItem[] = schedule.map((item) => {
    const paid = Math.min(left, item.amount);
    left -= paid;
    return { ...item, unpaid: item.amount - paid };
  });
  const overdueItems = unpaid.filter((i) => i.unpaid > 0 && i.dueMs < today);
  return {
    overdue: overdueItems.reduce((s, i) => s + i.unpaid, 0),
    oldestOverdueMs: overdueItems[0]?.dueMs || 0,
    overdueLabel: overdueItems[0]?.label || '',
    upcoming: unpaid.filter((i) => i.unpaid > 0 && i.dueMs >= today),
  };
}

export const daysBetween = (fromMs: number, toMs: number) => Math.max(0, Math.round((toMs - fromMs) / 86400000));
